import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Reveal, SectionLabel, LuxBg } from "../Reveal";
import { REPLAY_MANIFESTO_PARAGRAPH, ASCENSION_PATH, OFFER_STACK, GUARANTEE_TEXT, GUARANTEE_FINE_PRINT } from "../../replayContent";

gsap.registerPlugin(ScrollTrigger);

const LINES = [
  { text: "YOU'RE NOT STUCK", cls: "text-white" },
  { text: "BECAUSE OF YOUR", cls: "text-white/[0.28]" },
  { text: "CREDIT SCORE.", cls: "text-white" },
  { text: "YOU'RE STUCK BECAUSE OF", cls: "text-white/[0.28]" },
  { text: "BAD ADVICE.", cls: "text-[#FE0000]" },
];

export const ReplayManifesto = () => {
  const ref = useRef(null);

  useEffect(() => {
    const words = ref.current.querySelectorAll(".manifesto-word");
    const tween = gsap.fromTo(
      words,
      { opacity: 0, y: 24 },
      { opacity: 1, y: 0, stagger: 0.04, duration: 0.7, ease: "power3.out", scrollTrigger: { trigger: ref.current, start: "top 72%" } }
    );
    return () => { tween.scrollTrigger?.kill(); tween.kill(); };
  }, []);

  return (
    <section className="relative py-28 md:py-36 px-6 sm:px-10 lg:px-16 xl:px-24" data-testid="replay-manifesto">
      <LuxBg n={1} />
      <SectionLabel num="001" text="WHAT YOU JUST LEARNED" />
      <div ref={ref}>
        <h2 className="serif font-black uppercase tracking-[-0.02em] text-[clamp(36px,4.6vw,64px)]" style={{ lineHeight: 0.97 }} data-testid="replay-manifesto-statement">
          {LINES.map((l, li) => (
            <span key={li} className={`block ${l.cls}`}>
              {l.text.split(" ").map((w, wi) => (
                <span key={wi} className="manifesto-word inline-block mr-[0.28em]">{w}</span>
              ))}
            </span>
          ))}
        </h2>
      </div>
      <p className="text-white text-base leading-[1.75] max-w-[560px] mt-10" data-testid="replay-manifesto-paragraph">
        {REPLAY_MANIFESTO_PARAGRAPH}
      </p>
      <button
        data-testid="replay-manifesto-cta"
        data-cta
        onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
        className="mt-10 border border-[#FFE100] text-[#FFE100] font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-md btn-slide"
      >
        SEE HOW IT APPLIES TO YOU →
      </button>
    </section>
  );
};

export const AscensionPath = () => (
  <section id="path" className="border-y border-white/10" data-testid="ascension-path-section">
    <div className="grid md:grid-cols-3">
      {ASCENSION_PATH.map((s, i) => (
        <Reveal key={i} delay={i * 0.08} className={`relative overflow-hidden px-8 py-16 ${i < 2 ? "md:border-r border-b md:border-b-0 border-white/10" : ""}`}>
          <span className="mono absolute -top-4 -right-2 text-[120px] font-bold text-white/[0.04] select-none" aria-hidden="true">{s.num}</span>
          <div className="relative" data-testid={`path-step-${i}`}>
            <div className="mono text-[13px] tracking-[0.3em] text-[#FFC631] mb-4">STEP {s.num}</div>
            <h3 className="serif font-black uppercase text-2xl text-white mb-3">{s.name}</h3>
            <p className="text-sm text-white leading-relaxed">{s.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const OfferStack = () => (
  <section className="relative py-28 md:py-36 px-6 sm:px-10 lg:px-16 xl:px-24" data-testid="offer-stack-section">
    <LuxBg n={2} />
    <div className="max-w-3xl mx-auto">
      <Reveal><SectionLabel num="002" text="THE OFFER" /></Reveal>
      <Reveal delay={0.05}>
        <h2 className="serif font-black uppercase tracking-tight text-3xl md:text-5xl text-white mb-12">
          HERE&apos;S WHAT&apos;S WAITING <span className="text-[#FE0000]">WHEN YOU BOOK</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="rounded-xl border border-[#20416F] bg-[#20416F]/[0.12] p-8" data-testid="offer-stack-card">
          <ul className="space-y-5">
            {OFFER_STACK.map((o, i) => (
              <Reveal key={i} delay={0.12 + i * 0.08}>
                <li className="flex items-start justify-between gap-4" data-testid={`stack-item-${i}`}>
                  <span className="flex gap-3 text-[17px] text-white leading-snug">
                    <span className="text-[#FE0000] shrink-0">✓</span>
                    {o.item}
                  </span>
                  <span className="mono text-sm text-[#FE0000] shrink-0">Value {o.value}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <div className="border-t border-white/10 mt-8 pt-6">
            <div className="mono text-lg text-white line-through" data-testid="stack-total">$3,500 real value</div>
            <p className="serif text-xl text-white mt-3" data-testid="stack-reveal">
              Your investment today: <span className="text-[#FE0000]">a completed pre-approval application and one 15-minute call.</span>
            </p>
          </div>
          <p className="mono text-[13px] text-white mt-5 tracking-wider">EVERY ITEM IS LIVE, 1-ON-1 DELIVERY — NO PDFS, NO DOWNLOADS</p>
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <button
          data-testid="offer-stack-cta"
          data-cta
          onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
          className="mt-10 bg-[#FFE100] text-[#12264A] font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-md btn-slide"
        >
          ✓ BOOK MY FREE PRE-APPROVAL REVIEW
        </button>
      </Reveal>
    </div>
  </section>
);

export const Guarantee = () => (
  <section className="py-20 md:py-28 px-6" data-testid="guarantee-section">
    <div className="max-w-2xl mx-auto text-center">
      <Reveal><SectionLabel num="003" text="THE PROMISE" /></Reveal>
      <Reveal delay={0.05}>
        <div className="rounded-xl border-2 border-[#FE0000] bg-[#FE0000]/[0.04] p-10" data-testid="guarantee-card">
          <h2 className="serif font-black uppercase tracking-tight text-2xl md:text-3xl text-[#FE0000] mb-6">
            THE REG ASCENSION GUARANTEE
          </h2>
          <p className="text-[17px] text-white leading-[1.8]">{GUARANTEE_TEXT}</p>
        </div>
        <p className="text-[14px] text-white leading-relaxed mt-5 max-w-xl mx-auto" data-testid="guarantee-fine-print">
          {GUARANTEE_FINE_PRINT}
        </p>
      </Reveal>
    </div>
  </section>
);
